import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../api/client.ts";
import FormattedDate from "../../components/FormattedDate.tsx";
import ImageUploadField from "../../components/ImageUploadField.tsx";
import { OrgRoleChips, RoleChip } from "../../components/OrgRoles.tsx";
import Panel from "../../components/Panel.tsx";
import SettingRow from "../../components/SettingRow.tsx";
import ThemeModeToggle from "../../components/ThemeModeToggle.tsx";
import UserAvatar from "../../components/UserAvatar.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { humanize } from "../../utils/format.ts";

// Raster only: the crop re-encodes to WebP anyway, and the backend refuses SVG.
const PHOTO_TYPES = ["image/png", "image/jpeg", "image/webp"];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const savedName = user?.display_name ?? "";
  const [name, setName] = useState(savedName);

  // Follows a save (or an edit in another tab); a photo change leaves a draft alone.
  useEffect(() => setName(savedName), [savedName]);

  const save = useMutation({
    mutationFn: (display_name: string) => api.updateProfile({ display_name }),
    onSuccess: async () => {
      await refreshUser();
      toast.success("Profile updated");
    },
  });
  const uploadPhoto = useMutation({
    mutationFn: api.uploadAvatar,
    onSuccess: async () => {
      await refreshUser();
      toast.success("Photo updated");
    },
  });
  const removePhoto = useMutation({
    mutationFn: api.deleteAvatar,
    onSuccess: async () => {
      await refreshUser();
      toast.success("Photo removed");
    },
  });

  if (!user) return null;

  const trimmed = name.trim();
  const dirty = trimmed !== user.display_name;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (dirty && trimmed) save.mutate(trimmed);
  };
  const platformRoles = user.roles.filter((r) => r !== "user");

  return (
    <Stack spacing={3}>
      <Panel title="Photo" subtitle="Shown next to your name across Vayken">
        <ImageUploadField
          preview={
            <UserAvatar
              displayName={user.display_name}
              email={user.email}
              pictureUrl={user.picture_url}
              size={72}
              tooltip={false}
            />
          }
          hasImage={user.has_uploaded_picture}
          accept={PHOTO_TYPES}
          crop={{ shape: "circle" }}
          caption={
            user.has_uploaded_picture || !user.picture_url
              ? "PNG, JPG or WebP. You crop it to a square before it's saved."
              : "Your Google photo is shown until you upload one"
          }
          onUpload={uploadPhoto.mutateAsync}
          onRemove={removePhoto.mutateAsync}
          removeConfirm={{
            title: "Remove photo",
            body: user.auth_provider === "google"
              ? "Your Google photo is shown again, or your initials if you have none."
              : "Your initials are shown instead.",
          }}
        />
      </Panel>

      <Panel
        title="Details"
        subtitle={
          <>
            Member since <FormattedDate value={user.created_at} />
          </>
        }
      >
        <Stack component="form" spacing={2} onSubmit={submit} sx={{ maxWidth: 480 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            size="small"
            disabled={save.isPending}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            label="Email"
            value={user.email}
            size="small"
            disabled
            helperText="Your sign-in address. An admin can change it."
          />
          <Box>
            <Button type="submit" variant="contained" disabled={!dirty || !trimmed} loading={save.isPending}>
              Save
            </Button>
          </Box>
        </Stack>
      </Panel>

      {(user.organizations.length > 0 || platformRoles.length > 0) && (
        <Panel title="Access" subtitle="Where you're a member and what you can do there" disablePadding>
          {user.organizations.map((org) => (
            <SettingRow key={org.id} label={org.name} action={<OrgRoleChips roles={org.roles} />} />
          ))}
          {platformRoles.length > 0 && (
            <SettingRow
              label="Vayken platform"
              description="Staff roles across every organization"
              action={
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {platformRoles.map((r) => (
                    <RoleChip key={r} role={r} label={humanize(r)} />
                  ))}
                </Box>
              }
            />
          )}
        </Panel>
      )}

      <Panel title="Appearance" disablePadding>
        <SettingRow
          label="Theme"
          description="Saved in this browser"
          action={<ThemeModeToggle />}
        />
      </Panel>
    </Stack>
  );
}
